import { defineElement, FC, onDidLoad, useElement, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { useStore } from '@bpd-library/utilities';
import { Flyin } from '@molecules/fly-in/fly-in';
import {
    NotificationCard,
    NotificationCardInterface,
} from '@molecules/notification-card/notification-card';
import { closeFlyin, openFlyin } from '@store/flyins';
import { getSubscriptionState } from '@store/subscription/store';
import { userStore } from '@store/user';
import { daysToMilliseconds } from '@utilities/days-to-ms';
import { translateItem, useAreaData, useTranslate } from '@utilities/hooks';
import { useAreaSubscriptionLoader } from '@utilities/hooks/use-area-subscription-loader';
import Cookies from 'js-cookie';

const FLYIN_ID = 'flyin-user-journey';
const FLYIN_CLOSED = `${FLYIN_ID}-closed`;
const SALE_DATE_OUTPUT_CONFIG: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
};

interface FlyInUserJourneyInterface {
    enabled?: boolean;
}

const FlyinUserJourney: FC<FlyInUserJourneyInterface> = () => {
    const [enabled, , watchEnabled] = useProp('enabled', false);
    const cardContainer = useElement<HTMLDivElement>('[js-hook-fly-in-user-journey-card');
    const [, subscribeUserStore] = useStore(userStore);

    onDidLoad(() => {
        setTimeout(render, 300); // to avoid API calls on page load
    });

    watchEnabled(() => render());

    const subscriptionFlow = useAreaSubscriptionLoader();

    subscribeUserStore(() => {
        Cookies.remove(FLYIN_CLOSED);
        render();
    }, ['loggedIn']);

    const render = async () => {
        const isClosedByUser = Cookies.get(FLYIN_CLOSED);
        if (!enabled || !cardContainer.current || isClosedByUser) {
            return;
        }

        const { projectPhases } = await useAreaData();
        const { projectPhases: subscribedProjectIds } = getSubscriptionState();
        const cardData: NotificationCardInterface = {};
        const cardContent: Element = <p />;
        const selectedProjectPhases: string[] = [];

        const projectsSoonOnSale = projectPhases
            .filter(({ id, status, saleStartDate: saleStartDateStr }) => {
                if (
                    status !== 'InPreparation' ||
                    !saleStartDateStr ||
                    subscribedProjectIds.includes(id)
                ) {
                    return false;
                }
                const saleStartDate = new Date(saleStartDateStr);
                if (saleStartDate.valueOf() - Date.now() >= daysToMilliseconds(30)) return false;
                return true;
            })
            .map((project) => ({ ...project, saleStartDate: new Date(project.saleStartDate!) }));
        if (!projectsSoonOnSale.length) return;

        const project = projectsSoonOnSale[0];
        const { id, name, mainImage, saleStartDate } = project;
        if (!name || !mainImage?.url) return;

        selectedProjectPhases.push(id);

        const saleDateOutputStr = saleStartDate.toLocaleString('nl-NL', SALE_DATE_OUTPUT_CONFIG);
        cardContent.innerHTML = await translateItem('FlyIn.UserJourney.Content', {
            replace: [
                { id: 'projectName', value: name },
                { id: 'saleStartDate', value: saleDateOutputStr },
            ],
        });
        cardData.image = {
            image: mainImage.url,
            alt: mainImage.metadata?.altText ?? mainImage.title ?? '',
        };
        cardData.title = await translateItem('FlyIn.UserJourney.Title');
        cardData.contentLink = {
            label: await translateItem('FlyIn.UserJourney.CtaLabel'),
            onClick: async () => {
                closeFlyin(FLYIN_ID);

                const loaded = await subscriptionFlow.load();
                if (!loaded) return;

                subscriptionFlow.element.current?.openInitialized({ selectedProjectPhases });
            },
        };

        cardContainer.current.innerHTML = '';
        cardContainer.current.append(
            <NotificationCard {...cardData}>{cardContent}</NotificationCard>,
        );

        openFlyin(FLYIN_ID);
    };

    const setClosedCookie = () => Cookies.set(FLYIN_CLOSED, 'closed');

    return (
        <Flyin id={FLYIN_ID} label={useTranslate('Updates')} onClose={setClosedCookie}>
            <div js-hook-fly-in-user-journey-card />
        </Flyin>
    );
};

FlyinUserJourney.props = {
    enabled: {
        type: Boolean,
        reflectToAttr: true,
    },
};

defineElement('bpd-flyin-user-journey', FlyinUserJourney);
